// quickSendUtils.js
//
// Helpers for the Quick Send flow (4-step minimalist wizard).
//
//  * buildSendFormsPayload(state, practice) → payload accepted by
//    `POST /api/v1/practices/{id}/consent-requests/send-forms/`.
//  * mapChannelToApi(ch) — Quick Send uses the human-friendly 'COPY' alias
//    internally; the backend matrix expects 'COPY_LINK'.
//  * resolveDefaultTemplates(practice, channel) — reads the per-practice
//    default template ids stashed on practice.settings.
//  * buildFormUrl(accessToken, consentConfig) — same custom-subdomain
//    aware URL builder the legacy dialog uses; lifted out so step files
//    don't import the parent.
//
// Why a standalone file: keeps the orchestrator (`QuickSend.jsx`) thin
// and lets us cover the payload shape with a unit test in isolation.

import uuidv4 from 'src/utils/uuidv4';
import { buildResolvedDomainUrl } from 'src/utils/resolvedDomainUrl';

// Quick Send tab key → backend channel enum.
// The backend matrix uses COPY_LINK; the wizard uses COPY in its UI state
// because "Copy link" is shorter to read. We translate at the API edge.
export const mapChannelToApi = (channel) => {
    if (!channel) return null;
    if (channel === 'COPY') return 'COPY_LINK';
    return channel;
};

// Resolve the per-practice default template id for a given channel.
// Reads from `practice.settings` (the same place the legacy dialog reads
// from). Returns null when the practice hasn't configured a default.
export const resolveDefaultTemplates = (practice, channel) => {
    const settings = practice?.settings || {};
    if (channel === 'EMAIL') {
        return settings.consent_email_template || null;
    }
    if (channel === 'WHATSAPP') {
        return settings.consent_whatsapp_template || null;
    }
    return null;
};

// Quick Send's URL builder is now a thin delegate around the shared
// utility in src/utils/resolvedDomainUrl.js. The signature is preserved
// so step files calling ``buildFormUrl(accessToken, consentConfig)``
// continue to work without edits.
//
// Path prefix is "forms" to match the FORMS_V2 DomainUseCase
// ``path_prefix`` value (apps/domains/migrations/0002_seed_registry_and_use_cases.py).
// Previously this hardcoded "consent" which produced invalid links
// whenever a practice's Domain Management config had FORMS_V2 set to
// /forms/ (the configured default for the PATIENT_HUB registry). This
// fallback only fires when the BE response did NOT include ``form_url``;
// the BE itself now goes through ``build_url_for(practice, FORMS_V2, ...)``
// so production traffic should resolve via the BE-supplied URL and
// this fallback is only used by Dev / preview surfaces.
export const buildFormUrl = (accessToken, consentConfig) =>
    buildResolvedDomainUrl({
        consentConfig,
        pathPrefix: 'forms',
        token: accessToken,
    });

// Build the `send-forms` POST body from the wizard's local state.
//
// The wizard caller passes a flat `state` object so this helper is easy
// to test — we don't reach into a React hook scope.
//
// Required keys on `state`:
//   audienceMode:       'ONE_PATIENT' | 'ANONYMOUS'
//   channels:           e.g. ['COPY_LINK'] — caller already mapped.
//   selectedForms:      [{ id }, ...]
//   expiresInHours:     int (default 168)
//   selectedLead:       { id } | null
//   practiceFieldValues: { 'system:treated_by': {...}, ... } | {}
//
// Optional:
//   adHocRecipientEmail / adHocRecipientPhone — BE-4 anonymous path.
//   bcc                 — Array<string> of email addresses to BCC on the
//                          send (PR-10 item 6 — "BCC me a copy"). Empty
//                          / falsy entries are stripped; deduped.
//   idempotencyKey      — caller can pin one for retries; otherwise generated.
export const buildSendFormsPayload = (state) => {
    const {
        audienceMode = 'ONE_PATIENT',
        channels = [],
        selectedForms = [],
        expiresInHours = 168,
        selectedLead = null,
        practiceFieldValues = {},
        adHocRecipientEmail = null,
        adHocRecipientPhone = null,
        emailTemplateId = null,
        whatsappTemplateId = null,
        bcc = null,
        idempotencyKey = null,
        // Option A — when supplied, BE reuses these existing CRs
        // (created by the pre-flight) and dispatches them via the
        // requested channel instead of creating a new CR per send.
        // ONE access_token, multiple delivery channels. Null/empty
        // falls back to the create-and-dispatch path on the BE.
        consentRequestIds = null,
        // PDF fidelity toggle — defaults True (matches the BE serializer
        // default). Persisted onto the resulting ConsentRequest so the
        // public submit view knows whether to accept the client-rendered
        // PDF or hand off to the ReportLab fallback.
        highFidelityPdf = true,
    } = state || {};

    // De-dupe channels and apply the COPY → COPY_LINK alias once more
    // defensively so callers that forget the map still get a valid body.
    const apiChannels = Array.from(new Set(
        (channels || []).map((c) => mapChannelToApi(c)).filter(Boolean),
    ));

    const payload = {
        audience_mode: audienceMode,
        channels: apiChannels,
        form_ids: selectedForms.map((f) => Number(f.id)),
        expires_in_hours: Math.max(1, Number(expiresInHours) || 168),
        idempotency_key: idempotencyKey || uuidv4(),
        // Default True at the BE; only emit when explicitly disabled so
        // the payload stays small for the common (on) case. The BE
        // serializer treats missing as True via ``default=True``.
        ...(highFidelityPdf === false ? { high_fidelity_pdf: false } : {}),
    };

    if (emailTemplateId) payload.email_template_id = Number(emailTemplateId);
    if (whatsappTemplateId) payload.whatsapp_template_id = Number(whatsappTemplateId);

    // PR-10 item 6 — `bcc` is plumbed through to the send-forms endpoint
    // so practice users can opt in to receiving a copy of the outgoing
    // patient email. Strip empty entries + dedupe in case the same
    // address appears multiple times (e.g. user is in a shared BCC list
    // AND ticks the "BCC me" checkbox).
    if (Array.isArray(bcc) && bcc.length) {
        const cleanedBcc = Array.from(new Set(
            bcc.map((v) => (typeof v === 'string' ? v.trim() : ''))
                .filter(Boolean),
        ));
        if (cleanedBcc.length) {
            payload.bcc = cleanedBcc;
        }
    }

    if (audienceMode === 'ONE_PATIENT' && selectedLead?.id) {
        payload.lead_id = Number(selectedLead.id);
    }

    // Option A — thread the pre-flight CR ids through to the BE so the
    // create-and-dispatch path skips ConsentRequest.objects.create and
    // reuses the existing CRs for THIS channel's dispatch. Only sent
    // when the FE has CRs to reuse (i.e. the pre-flight ran and
    // produced ids); falls back to the create path otherwise.
    if (
        audienceMode === 'ONE_PATIENT'
        && Array.isArray(consentRequestIds)
        && consentRequestIds.length > 0
    ) {
        payload.consent_request_ids = consentRequestIds
            .map((v) => Number(v))
            .filter((v) => Number.isFinite(v) && v > 0);
    }

    // ANONYMOUS + ad-hoc recipient (BE-4). Only ship the field when it
    // has a value — the serializer treats null and missing as equivalent
    // but we'd rather not send junk.
    if (audienceMode === 'ANONYMOUS') {
        if (adHocRecipientEmail) payload.ad_hoc_recipient_email = adHocRecipientEmail;
        if (adHocRecipientPhone) payload.ad_hoc_recipient_phone = adHocRecipientPhone;
    }

    // Strip empty practice-field rows so the BE doesn't try to render
    // empty strings onto the generated PDF.
    if (practiceFieldValues && Object.keys(practiceFieldValues).length > 0) {
        const cleaned = Object.fromEntries(
            Object.entries(practiceFieldValues).filter(
                ([, v]) => v && v.value !== '' && v.value != null,
            ),
        );
        if (Object.keys(cleaned).length > 0) {
            payload.practice_field_values = cleaned;
        }
    }

    return payload;
};

// PR-10 item 8 — turn an underscore-delimited Meta template name (e.g.
// "complete_form") into a friendly Title Case label ("Complete Form").
// Only used for the WhatsApp template dropdown fallback when neither
// `t.title` nor `t.name` carries human-authored copy. The BE value is
// untouched — this is presentation-only.
export const humanizeSlug = (slug) => {
    if (typeof slug !== 'string' || !slug.trim()) return '';
    return slug
        .replace(/[_-]+/g, ' ')
        .trim()
        .split(/\s+/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
        .join(' ');
};

// PR-10 item 8 — display label for a WhatsApp template row, with a hard
// fallback to `Template {id}` so the dropdown never shows raw slug text
// to the practice user. `t.id` remains the value passed to the BE.
export const whatsappTemplateLabel = (t) => {
    if (!t) return '';
    if (t.title && typeof t.title === 'string' && t.title.trim()) return t.title;
    if (t.name && typeof t.name === 'string' && t.name.trim()) return humanizeSlug(t.name);
    return `Template ${t.id || ''}`.trim();
};

// Tiny validator used by the Step-4 ad-hoc-email field.
export const isValidEmail = (val) =>
    typeof val === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

// Loose phone validator — accepts +, digits, spaces, dashes. Enough for
// the FE to surface a "looks wrong" error; the backend has the real check.
export const isValidPhone = (val) => {
    if (typeof val !== 'string') return false;
    const cleaned = val.replace(/[\s-]/g, '');
    return /^\+?\d{6,16}$/.test(cleaned);
};
